'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

interface Props {
  tags: string[];
  active?: string;
}

/**
 * Filtro de tags do blog. Mostra os chips em no máximo 2 linhas; o excedente
 * fica escondido atrás de um badge "+N" que expande ao clicar.
 */
export function TagFilter({ tags, active }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [clipHeight, setClipHeight] = useState<number | null>(null);

  useEffect(() => {
    const wrap = ref.current;
    if (!wrap) return;
    function measure() {
      if (!wrap) return;
      const chips = Array.from(wrap.querySelectorAll<HTMLElement>('[data-chip]'));
      if (chips.length === 0) {
        setHiddenCount(0);
        setClipHeight(null);
        return;
      }
      const firstChip = chips[0];
      if (!firstChip) return;
      const baseTop = firstChip.offsetTop;
      const lineH = firstChip.offsetHeight;
      const line3Top = baseTop + lineH * 2 + 6;
      let clip = baseTop + lineH;
      let hidden = 0;
      for (const c of chips) {
        if (c.offsetTop < line3Top - 2) {
          clip = Math.max(clip, c.offsetTop + c.offsetHeight);
        } else {
          hidden += 1;
        }
      }
      setHiddenCount(hidden);
      setClipHeight(clip);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [tags]);

  const collapsed = !expanded && hiddenCount > 0;

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        ref={ref}
        className="tagbar"
        style={collapsed && clipHeight ? { maxHeight: clipHeight, overflow: 'hidden' } : undefined}
      >
        <Link href={'/blog' as never} data-chip className={active ? 'tagchip' : 'tagchip on'}>
          Todos
        </Link>
        {tags.map((t) => (
          <Link
            key={t}
            href={`/blog?tag=${encodeURIComponent(t)}` as never}
            data-chip
            className={active === t ? 'tagchip on' : 'tagchip'}
          >
            {t}
          </Link>
        ))}
      </div>
      {hiddenCount > 0 ? (
        <button type="button" className="tagchip more" onClick={() => setExpanded((e) => !e)}>
          {expanded ? 'mostrar menos' : `+${hiddenCount}`}
        </button>
      ) : null}
    </div>
  );
}
